import { useMemo } from 'react'

import type { Participant } from 'livekit-client'
import { participantPermissionObserver } from '@livekit/components-core'
import type { ParticipantPermission } from '@livekit/protocol'

import { useEnsureParticipant } from '@/src/context'
import { useObservableState } from '@/src/hooks/internal/useObservableState'

/**
 * The `useParticipantPermissions` hook returns the permissions of a given participant.
 *
 * @example
 * ```tsx
 * const permissions = useParticipantPermissions({ participant });
 * ```
 * @public
 */
export interface UseParticipantPermissionsOptions {
  participant?: Participant
}

/** @public */
export function useParticipantPermissions(
  options: UseParticipantPermissionsOptions = {},
): ParticipantPermission | undefined {
  const p = useEnsureParticipant(options.participant)
  const permissionObserver = useMemo(() => participantPermissionObserver(p), [p])
  const permissions = useObservableState(permissionObserver, p.permissions)
  return permissions
}
